import { getDatetimeFromDateAndTimeStrings } from '@/utils/time'
import { DateTime } from 'luxon'
import { Trip } from '@/classes/Trip'
import { TripAddress } from '@/classes/TripAddress'
import { Address } from '@/classes/Address'
import { Note } from '@/classes/Note'
import { TripVehicleGroup } from '@/classes/TripVehicleGroup'
import { TripVehicle } from '@/classes/TripVehicle'
import { TripDate } from '@/classes/TripDate'
import { store } from '@/state/store'
import { PaymentTypeId, PaymentStageKey, PricingMethod, ChargeTypeId, ChargeTypeKey } from '@/utils/enum'
import { deepClone } from '@/utils/deepClone'
import {
  hasTripType,
  hasVehicleQuantitiesAndTypes,
  hasPickupStopWithAddressAndDate,
  setTripFieldsForCirculation,
} from '@/utils/trip'

export const scrubQuoteForAPI = (quote, convertTripIdToIndex = false, capturePaymentStages = false) => {
  quote.customerId = quote.customer.id
  quote.priceForAllPricingMethods = true

  if (quote.customer?.bookings) {
    if (quote.customer.bookings === '--') {
      quote.customer.bookings = null
    }
  }

  if (quote.leadSource?.id) {
    quote.leadSourceTypeId = quote.leadSource.id
  }

  quote.leadSourceType = quote.leadSource

  if (quote.checkoutPage?.id) {
    quote.checkoutPageId = quote.checkoutPage.id
  }

  let tripIndex = 0
  quote.trips.forEach((trip) => {
    // Preserve checkoutTypeId from nested object before cleanup
    trip.checkoutTypeId = trip.checkoutTypeId ?? trip.checkoutType?.id
    delete trip.tripContacts
    if (convertTripIdToIndex) {
      trip.tripId = tripIndex
    }

    if (quote.pricingMethod === PricingMethod.SinglePrice) {
      trip.vehicleNeededEntireTrip = false
    }

    trip.customerId = quote.customerId || trip.customerId
    trip.vehicles = trip.requiredVehicles
    for (const vehicle of trip.vehicles) {
      if (vehicle.vehicleType?.id) {
        vehicle.vehicleTypeId = vehicle.vehicleType?.id
      }
    }
    delete trip.requiredVehicles
    trip.tripContactId = trip?.tripContactIsCustomer ? quote.customerId : trip?.tripContact?.customerId
    for (const rate of trip.rates) {
      rate.rateTypeId = rate.rateType.id
    }
    // Ensure charges have proper format with chargeType object and chargeTypeId
    for (const charge of trip.charges) {
      if (charge.chargeType) {
        // Set chargeTypeId from chargeType.id
        if (charge.chargeType.id) {
          charge.chargeTypeId = charge.chargeType.id
        }

        // Ensure amount, percent, and fixedAmount fields exist (can be null)
        if (charge.amount === undefined) {
          charge.amount = null
        }
        if (charge.percent === undefined) {
          charge.percent = null
        }
        if (charge.fixedAmount === undefined) {
          charge.fixedAmount = null
        }
      }
    }

    // For new charge model (bid/category quotes), add processing fee, discount, and markup
    // from trip properties to the charges array if they're not already there
    // Helper function to check if a charge type already exists
    const hasChargeType = (chargeTypeKey) => {
      return trip.charges.some((charge) => charge.chargeType?.key === chargeTypeKey)
    }

    // Add processing fee if present and not already in charges
    if (trip.processingFeePercentage > 0 && !hasChargeType(ChargeTypeKey.ProcessingFee)) {
      trip.charges.push({
        chargeType: {
          id: ChargeTypeId.ProcessingFee,
          key: ChargeTypeKey.ProcessingFee,
          label: 'Processing Fees',
        },
        chargeTypeId: ChargeTypeId.ProcessingFee,
        percent: trip.processingFeePercentage,
        amount: null,
        fixedAmount: null,
      })
    }

    if (quote.pricingMethod === 'bids' || quote.pricingMethod === 'category') {
      // Add marketplace discount if present and not already in charges
      if (trip.marketplaceDiscountPercent > 0 && !hasChargeType(ChargeTypeKey.Discount)) {
        trip.charges.push({
          chargeType: {
            id: ChargeTypeId.Discount,
            key: ChargeTypeKey.Discount,
            label: 'Discount',
          },
          chargeTypeId: ChargeTypeId.Discount,
          percent: trip.marketplaceDiscountPercent,
          amount: null,
          fixedAmount: null,
        })
      }

      // Add marketplace markup if present and not already in charges
      if (trip.marketplaceMarkupPercent > 0 && !hasChargeType(ChargeTypeKey.Markup)) {
        trip.charges.push({
          chargeType: {
            id: ChargeTypeId.Markup,
            key: ChargeTypeKey.Markup,
            label: 'Markup',
          },
          chargeTypeId: ChargeTypeId.Markup,
          percent: trip.marketplaceMarkupPercent,
          amount: null,
          fixedAmount: null,
        })
      }

      trip.charges = trip.charges.map((charge) => ({
        ...charge,
        // Preserve percentage if it exists, otherwise set to null
        percent: charge.percent || null,
        // Always clear the deprecated 'amount' field
        amount: null,
        // For percentage charges: clear fixedAmount
        // For fixed charges: migrate 'amount' to 'fixedAmount' if needed
        fixedAmount: charge.percent ? null : charge.fixedAmount || charge.amount,
      }))
    }

    // Filter out charges where both percent and fixedAmount are null or undefined
    // We were seeing base_fares where these values were not set after splitting trip
    trip.charges = trip.charges.filter(
      (charge) =>
        (charge.percent !== null && charge.percent !== undefined) ||
        (charge.fixedAmount !== null && charge.fixedAmount !== undefined),
    )

    if (trip.hasCirculation && trip.stops?.length > 0) {
      trip = setTripFieldsForCirculation(trip)
    }
    if (trip?.stops) {
      for (const stop of trip.stops) {
        if (stop.address) {
          stop.address.name = stop.address.addressName

          if (stop.address.postalCode === null) {
            stop.address.postalCode = ''
          }
        }

        if (!stop.active) {
          stop.active = true
        }

        const timeZone = stop?.address?.timeZone
        if (stop.pickupDate) {
          stop.pickupDatetime = getDatetimeFromDateAndTimeStrings(stop.pickupDate, stop.pickupTime, timeZone)
        }
        if (stop.spotTime?.spotTime) {
          stop.spotTime.spotTime = DateTime.fromISO(stop.spotTime.spotTime)
            .setZone(timeZone, { keepLocalTime: true })
            .toISO()
        }

        if (stop.dropoffDate) {
          stop.dropoffDatetime = getDatetimeFromDateAndTimeStrings(stop.dropoffDate, stop.dropoffTime, timeZone)
        }
      }
    }

    if (capturePaymentStages && trip.paymentType.id) {
      const isBalanceMethodsEnabled = isBalancePaymentMethodEnabled(trip)
      const checkoutPaymentMethods = trip?.stagePaymentMethods?.checkoutPaymentMethods || []
      const balancePaymentMethods = trip?.stagePaymentMethods?.balancePaymentMethods || []
      const allowedPaymentMethodsMap = {}

      for (const checkoutPaymentMethod of checkoutPaymentMethods) {
        if (typeof checkoutPaymentMethod.isAllowed === 'boolean') {
          checkoutPaymentMethod.isAllowed = checkoutPaymentMethod.isAllowed ? 1 : 0
        }
        checkoutPaymentMethod.paymentStage = PaymentStageKey.Checkout
        allowedPaymentMethodsMap[checkoutPaymentMethod.paymentMethodId] = checkoutPaymentMethod.isAllowed
      }

      if (isBalanceMethodsEnabled) {
        for (const balancePaymentMethod of balancePaymentMethods) {
          if (typeof balancePaymentMethod.isAllowed === 'boolean') {
            balancePaymentMethod.isAllowed = balancePaymentMethod.isAllowed ? 1 : 0
          }
          balancePaymentMethod.paymentStage = PaymentStageKey.RemainingBalance
        }
      } else {
        trip.stagePaymentMethods.balancePaymentMethods = deepClone(checkoutPaymentMethods)
        trip.stagePaymentMethods.balancePaymentMethods.forEach((method) => {
          method.paymentStage = PaymentStageKey.RemainingBalance
        })
      }

      for (const paymentMethod of trip.paymentMethods) {
        paymentMethod.paymentMethodTypeId = paymentMethod.paymentMethodType.id
        paymentMethod.isAllowed = allowedPaymentMethodsMap[paymentMethod.paymentMethodTypeId]
      }
    } else {
      delete trip.stagePaymentMethods
      if (trip?.paymentMethods) {
        for (const paymentMethod of trip.paymentMethods) {
          paymentMethod.paymentMethodTypeId = paymentMethod.paymentMethodType.id
          paymentMethod.isAllowed = paymentMethod.isAllowed ? 1 : 0
        }
      }
    }

    if (trip?.tripType?.id) {
      trip.tripTypeId = trip.tripType.id
    }
    if (trip?.paymentType?.id) {
      trip.paymentTypeId = trip.paymentType.id
    }

    if (trip?.tripEventType?.tripEventTypeId) {
      trip.tripEventTypeId = trip.tripEventType.tripEventTypeId
    }

    tripIndex++
  })
  return quote
}

export const convertTripDTOsToRouteTrips = (trips) => {
  if (!trips) {
    return []
  }
  let newTrips = []
  for (const tripData of trips) {
    const trip = new Trip()
    trip.tripId = tripData.tripId
    trip.routeName = tripData.routeName
    trip.tripTypeId = tripData.tripTypeId
    trip.tripEventTypeId = tripData.tripEventTypeId
    trip.passengerCount = tripData.passengerCount
    trip.requiredDrivers = tripData.requiredDrivers
    trip.description = tripData.description
    trip.notes = tripData.notes
    trip.isTripContactSelected = tripData.istripContactSelected
    trip.processingFeePercentage = tripData.processingFeePercentage
    trip.tripContact = tripData.tripContact
    trip.tripContactIsCustomer =
      tripData.tripContact?.customerId !== null && tripData.tripContact?.customerId === tripData.customer?.customerId
    trip.tripNotes = tripData.tripNotes
    trip.rates = tripData.rates
    trip.charges = tripData.charges
    trip.recurrences = tripData.recurrences
    trip.paymentMethods = tripData.paymentMethods
    trip.paymentType = tripData.paymentType
    trip.paymentTypeId = tripData.paymentTypeId
    trip.dueDate = tripData.dueDate
    trip.depositPercentage = tripData.depositPercentage
    trip.pricingMarket = tripData.pricingMarket

    trip.tripAddresses = convertTripAddressDTOsToTripAddresses(tripData.tripAddresses)
    trip.tripVehicleGroups = convertTripVehicleGroupDTOsToTripVehicleGroups(tripData.tripVehicleGroups, tripData.stops)

    newTrips.push(trip)
  }
  return newTrips
}

export const convertTripAddressDTOsToTripAddresses = (tripAddressDTOs) => {
  const tripAddresses = []
  for (const [tripAddressIndex, tripAddressData] of tripAddressDTOs.entries()) {
    const tripAddressHash = tripAddressData.tripAddressHash || tripAddressData.tripAddressId
    const tripAddress = new TripAddress({
      tripAddressHash: tripAddressHash.toString(),
      orderIndex: tripAddressIndex,
      tripAddressId: tripAddressData.tripAddressId,
    })
    const address = new Address(tripAddressData.address)
    tripAddress.address = address
    if (tripAddressData.tripAddressNotes?.[0]) {
      tripAddress.tripAddressNotes[0] = new Note(tripAddressData.tripAddressNotes[0])
    }
    tripAddresses.push(tripAddress)
  }
  return tripAddresses
}

export const convertTripVehicleGroupDTOsToTripVehicleGroups = (tripVehicleGroupDTOs, stopDTOs) => {
  const vehicleTypes = store.getters['types/vehicleTypes']
  let tripVehicleGroups = []
  for (const vehicleGroupData of tripVehicleGroupDTOs) {
    const tripVehicleGroupHash =
      vehicleGroupData.tripVehicleGroupHash?.toString() || vehicleGroupData.tripVehicleGroupId?.toString()
    const tripVehicleGroup = new TripVehicleGroup({
      tripVehicleGroupHash,
      tripVehicleGroupIndex: tripVehicleGroups.length,
      tripVehicleGroupId: vehicleGroupData.tripVehicleGroupId,
      parentTripVehicleGroupId: vehicleGroupData.parentTripVehicleGroupId,
    })
    let tripVehicles = []
    for (const tripVehicleData of vehicleGroupData.tripVehicles) {
      const tripVehicle = new TripVehicle({
        vehicleTypeId: tripVehicleData.vehicleTypeId,
        quantity: tripVehicleData.quantity,
        vehicleOrderIndex: null,
        vehicleTypes,
        tripVehicleId: tripVehicleData.tripVehicleId,
      })
      tripVehicles.push(tripVehicle)
    }
    tripVehicleGroup.tripVehicles = tripVehicles
    const filteredStops = stopDTOs.filter((stop) => {
      const id = stop.tripVehicleGroupHash || stop.tripVehicleGroupId
      return id?.toString() === tripVehicleGroupHash
    })
    tripVehicleGroup.tripVehicleGroupStops = filteredStops
    let datetimesByTripAddressHash = {}
    for (const stop of filteredStops) {
      const tripAddressHash = stop.tripAddressHash?.toString() || stop.tripAddressId?.toString()
      if (stop.pickupDate || stop.pickupDatetime) {
        if (tripAddressHash && !datetimesByTripAddressHash?.hasOwnProperty(tripAddressHash)) {
          datetimesByTripAddressHash[tripAddressHash] = {}
        }
        const date =
          stop.pickupDate ||
          DateTime.fromISO(stop.pickupDatetime, {
            zone: stop.address.timeZone,
          }).toFormat('yyyy-MM-dd')
        if (!datetimesByTripAddressHash[tripAddressHash]?.hasOwnProperty(date)) {
          datetimesByTripAddressHash[tripAddressHash][date] = []
        }
        const time =
          stop.pickupTime ||
          DateTime.fromISO(stop.pickupDatetime, {
            zone: stop.address.timeZone,
          }).toFormat('HH:mm')
        datetimesByTripAddressHash[tripAddressHash][date].push(time)
      } else if (stop.dropoffDate || stop.dropoffDatetime) {
        const finalDropoffDate = {
          tripAddressHash: tripAddressHash,
          date:
            stop.dropoffDate ||
            DateTime.fromISO(stop.dropoffDatetime, {
              zone: stop.address.timeZone,
            }).toFormat('yyyy-MM-dd'),
          time:
            stop.dropoffTime ||
            DateTime.fromISO(stop.dropoffDatetime, {
              zone: stop.address.timeZone,
            }).toFormat('HH:mm'),
        }
        tripVehicleGroup.finalDropoffDate = finalDropoffDate
      }
    }
    for (const [tripAddressHash, timesByDate] of Object.entries(datetimesByTripAddressHash)) {
      for (const [date, times] of Object.entries(timesByDate)) {
        let tripDate = new TripDate(tripAddressHash)
        tripDate.date = date
        tripDate.times = times
        tripVehicleGroup.tripDates.push(tripDate)
      }
    }
    tripVehicleGroup.name = vehicleGroupData.name
    tripVehicleGroups.push(tripVehicleGroup)
  }
  if (tripVehicleGroups.length) {
    tripVehicleGroups[0].selected = true
  }
  return tripVehicleGroups
}

export const isBalancePaymentMethodEnabled = (trip): boolean => {
  return trip.paymentType.id === PaymentTypeId.DownPayment
}

export const isMarketplaceQuote = (quote): boolean => {
  return [PricingMethod.Bids, PricingMethod.Category].includes(quote?.pricingMethod)
}

/**
 * Determines whether a quote can be automatically priced in the quote form
 * using basic requirements of the included trips – each has a trip type, all required
 * vehicles have quantities and vehicle types, and there is at least one stop with
 * both an address and date.
 * @param quote – A quote being created in the quote form.
 * @returns `true` if a quote should be sent for pricing, `false` otherwise.
 */
export const shouldAutoPriceQuote = (quote): boolean => {
  return quote.trips.every(
    (trip) => hasTripType(trip) && hasVehicleQuantitiesAndTypes(trip) && hasPickupStopWithAddressAndDate(trip),
  )
}

/**
 * Builds a charge object for line items API with percentage-based charging
 */
const buildPercentChargeForLineItems = (chargeTypeKey: string, percent: number | undefined) =>
  buildGeneralChargeForLineItems(chargeTypeKey, undefined, percent)

/**
 * Builds a charge object for line items API
 */
const buildGeneralChargeForLineItems = (
  chargeTypeKey: string,
  amount: number | undefined,
  percent: number | undefined,
) => ({
  type: chargeTypeKey,
  amount: null,
  percent: percent ?? null,
  fixedAmount: amount ?? null,
})

/**
 * Builds the complete charges array for a trip to send to line items API
 * Includes base charges and percentage-based charges (processing fee, discount, markup)
 */
export const buildTripChargesForLineItems = (trip) => {
  // Add base charges from the trip
  const charges = (trip.charges ?? [])
    .filter((charge) => charge.chargeType?.key)
    .map((charge) =>
      buildGeneralChargeForLineItems(charge.chargeType.key, charge.fixedAmount || charge.amount, charge.percent),
    )

  // Add percentage-based charges (processing fee, discount, markup)
  const percentCharges = [
    {
      condition: trip.processingFeePercentage > 0,
      chargeTypeKey: ChargeTypeKey.ProcessingFee,
      percent: trip.processingFeePercentage,
    },
    {
      condition: trip.marketplaceDiscountPercent > 0,
      chargeTypeKey: ChargeTypeKey.Discount,
      percent: trip.marketplaceDiscountPercent,
    },
    {
      condition: trip.marketplaceMarkupPercent > 0,
      chargeTypeKey: ChargeTypeKey.Markup,
      percent: trip.marketplaceMarkupPercent,
    },
  ]

  for (const { condition, chargeTypeKey, percent } of percentCharges) {
    if (condition) {
      charges.push(buildPercentChargeForLineItems(chargeTypeKey, percent))
    }
  }

  return charges
}
